import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRefundAccess } from '../hooks/useRefundAccess';
import { useMasterData } from '../context/MasterDataContext';

/**
 * Route Guard khusus untuk memblokir akses URL /pengembalian
 * jika toko/cabang saat ini tidak memiliki izin akses return/refund.
 * Jika diblokir:
 * - Menampilkan notifikasi toast error bahwa akses ditolak.
 * - Me-redirect user ke halaman kasir masing-masing (/penjualan, /pembelian, atau /).
 */
export default function RefundProtectedRoute({ children }) {
  const { isRefundAllowed, currentCabangName, currentCabangId, role } = useRefundAccess();
  const { isLoadingMasterData, isLoaded } = useMasterData() || {};
  const location = useLocation();

  useEffect(() => {
    // Tampilkan notifikasi jika sudah selesai inisialisasi dan akses tidak diizinkan
    if (isLoaded && !isRefundAllowed) {
      toast.error(
        `Akses ditolak: Menu pengembalian dinonaktifkan untuk ${
          currentCabangName || (currentCabangId ? `cabang ID: ${currentCabangId}` : 'toko ini')
        }`,
        {
          toastId: 'refund-blocked-access',
          autoClose: 3000,
        }
      );
    }
  }, [isLoaded, isRefundAllowed, currentCabangName, currentCabangId]);

  // Jika master data pertama kali masih loading dan belum ada cache
  if (isLoadingMasterData && !isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Jika toko tidak diizinkan, blokir akses URL dan redirect
  if (!isRefundAllowed) {
    const fallbackPath =
      role === 'kasir'
        ? '/penjualan'
        : role === 'kasir_pembelian'
        ? '/pembelian'
        : '/';

    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children;
}
